'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { TextButton } from '@flipster/react/design-system/text-button';
import Formatted from '@flipster/react/design-utils/formatted';
import { NoSSR } from '@flipster/react/misc/lifecycle/NoSSR';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { css } from '@flipster/styled-system/css';
import { center, flex, stack, vstack } from '@flipster/styled-system/patterns';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { convertWebviewToWebUrl } from '@flipster/utils/env-helper';
import segment from '@flipster/utils/segment';
import { ReferralPageEvent } from '@flipster/utils/segment/events/referral';

import { SegmentPage } from '../misc/segment/segment-page';
import { useAuth } from '../shared/auth/state';
import { useIsWebview } from '../shared/webview';
import { zendesk } from '../shared/zendesk/url';
import referrerBannerImg from './_assets/referrer-banner.webp';
import { ReferralCodeDesktop, ReferralCodeMobileFooter } from './_referral-code-copy';
import { RewardCardTradingFee } from './_reward-trading-fee';
import { referrerPageBunja, referrerPageSegmentBunja } from './bunja';

interface Props {
  hasFooter?: boolean;
}

const ReferrerPage = ({ hasFooter = false }: Props) => {
  const { isMobile } = useBreakpoint();
  const isWebview = useIsWebview();
  const { isLoggedIn, isKol } = useAuth();
  const { totalRewardAtom, isLoadingAtom } = useBunja(referrerPageBunja);
  const { commonEventPayloadAtom } = useBunja(referrerPageSegmentBunja);
  const totalReward = useAtomValue(totalRewardAtom);
  const payload = useAtomValue(commonEventPayloadAtom);
  const isLoading = useAtomValue(isLoadingAtom);

  return (
    <>
      {!isLoading && <SegmentPage name={ReferralPageEvent.ReferralPageView} context={payload} />}
      <div
        className={stack({
          position: 'relative',
          gap: 32,
          pb: isWebview ? 180 : 32,
          pt: 16,
          px: { base: 16, smallDesktop: 0 },
        })}
      >
        <section className={vstack({ gap: { base: 16, smallDesktop: 32 } })}>
          <div
            className={flex({
              flexDir: { base: 'column', smallDesktop: 'row' },
              width: '100%',
              gap: { base: 16, smallDesktop: 32 },
              align: 'center',
              justify: 'space-between',
            })}
          >
            <header className={stack({ gap: 4, textAlign: 'left' })}>
              <h1 className={css({ textStyle: 'Display/XL', color: 'inverse.white' })}>
                <FormattedMessage id='Referral.r250401.Share_card_invite_and_reward_description' />
              </h1>
              <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
                <FormattedMessage id='Autogen_common.r251126.s_Unlimited_invitation' />
              </p>
            </header>
            <img src={referrerBannerImg.src} alt='referrer-banner' className={css({ w: 230, h: 210 })} />
          </div>
          <div className={stack({ gap: 12, _empty: { display: 'none' } })}>
            {totalReward && (
              <p>
                <span>
                  <FormattedMessage id='Autogen_common.r251126.s_You_ve_earned' />
                </span>{' '}
                <Formatted.Number
                  toFixed
                  value={totalReward}
                  suffix={UsdtCurrency}
                  precision={UsdtPrecision}
                  css={{ textStyle: 'Text/M/Monospace/Strong', color: 'content.accent.info' }}
                />
              </p>
            )}
            {/* /support/affiliate 페이지가 모바일 친화적이지 않은 관계로, 현재는 kol이 아닌 유저에게만 보이지만, 추후 kol 유저에게도 보이도록 수정 필요 */}
            {/* context: https://prextech.slack.com/archives/C01D2R6359N/p1741237617909829 */}
            {isLoggedIn && !isKol && (
              <Button
                hierarchy='primary'
                color='neutral'
                as='a'
                css={{ minW: { smallDesktop: 328 } }}
                href={convertWebviewToWebUrl('/support/referral')}
                onClick={() => segment.track(ReferralPageEvent.ReferralPageViewMyReferralClick, payload)}
                label={<FormattedMessage id='Autogen_common.r251126.s_View_my_referrals' />}
              />
            )}
          </div>
        </section>
        <div className={stack({ gap: 16 })}>
          {!isMobile && <ReferralCodeDesktop />}
          <RewardCardTradingFee />
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Rewards_are_distributed_daily' />
          </p>
          <div className={center()}>
            <NoSSR>
              <TextButton
                color='accent'
                size='large'
                as='a'
                newTab
                href={zendesk.getUrl('ReferralProgram')}
                onClick={() => segment.track(ReferralPageEvent.ReferralLearnMoreClick, { refer_type: 'referrer' })}
                label={<FormattedMessage id='Autogen_common.r251126.s_Learn_more' />}
              />
            </NoSSR>
          </div>
        </div>
        {isMobile && hasFooter && <ReferralCodeMobileFooter />}
      </div>
    </>
  );
};

export default ReferrerPage;
